import React from 'react';
import { useTranslation } from 'react-i18next';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import { Switch } from '../ui/switch';
import {
  TAX_BASIS_EXCLUSIVE,
  TAX_BASIS_INCLUSIVE,
  TAX_TREATMENT_APPLICABLE,
  TAX_TREATMENT_EXEMPT,
  validateProviderTax,
} from '../../utils/providerTax';

// TREATX-TAX-5 — provider treatment VAT, inside the existing price step.
// Four inputs, no wizard, no suggested rate: TreatX is a marketplace, not a
// tax adviser, so an unverified rate hint would read as advice it cannot give.
const Choice = ({ active, onClick, children, testId }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testId}
    className={`px-3 py-1.5 rounded-full text-sm transition-colors ${
      active ? 'bg-slate-900 text-white' : 'bg-white text-slate-600 border border-slate-200 hover:border-slate-400'
    }`}
  >
    {children}
  </button>
);

export const ProviderTaxSection = ({ data, onChange, currencySym }) => {
  const { t } = useTranslation();
  const exempt = data.taxTreatment === TAX_TREATMENT_EXEMPT;
  const set = (patch) => onChange({ ...data, ...patch });

  return (
    <div className="rounded-lg border border-slate-200 bg-slate-50/60 p-4 space-y-4" data-testid="provider-tax-section">
      <div className="flex items-start justify-between gap-4">
        <div>
          <Label className="text-sm font-semibold">{t('offer.tax.title')}</Label>
          <p className="text-xs text-slate-500 mt-0.5" data-testid="provider-tax-responsibility">
            {t('offer.tax.responsibility')}
          </p>
        </div>
        <Switch
          checked={!!data.taxDeclared}
          onCheckedChange={(v) => set({ taxDeclared: v })}
          data-testid="provider-tax-declared-toggle"
        />
      </div>

      {data.taxDeclared && (
        <div className="space-y-4" data-testid="provider-tax-inputs">
          <div>
            <Label className="text-xs text-slate-600">{t('offer.tax.basis')}</Label>
            <div className="flex gap-2 mt-1">
              <Choice
                active={data.taxBasis === TAX_BASIS_INCLUSIVE}
                onClick={() => set({ taxBasis: TAX_BASIS_INCLUSIVE })}
                testId="provider-tax-basis-inclusive"
              >{t('offer.tax.basisInclusive')}</Choice>
              <Choice
                active={data.taxBasis === TAX_BASIS_EXCLUSIVE}
                onClick={() => set({ taxBasis: TAX_BASIS_EXCLUSIVE })}
                testId="provider-tax-basis-exclusive"
              >{t('offer.tax.basisExclusive')}</Choice>
            </div>
          </div>

          <div>
            <Label className="text-xs text-slate-600">{t('offer.tax.treatment')}</Label>
            <div className="flex gap-2 mt-1">
              <Choice
                active={!exempt}
                onClick={() => set({ taxTreatment: TAX_TREATMENT_APPLICABLE })}
                testId="provider-tax-treatment-applicable"
              >{t('offer.tax.treatmentApplicable')}</Choice>
              <Choice
                active={exempt}
                onClick={() => set({ taxTreatment: TAX_TREATMENT_EXEMPT })}
                testId="provider-tax-treatment-exempt"
              >{t('offer.tax.treatmentExempt')}</Choice>
            </div>
          </div>

          {exempt ? (
            <div>
              <Label className="text-xs text-slate-600">{t('offer.tax.exemptionReason')} *</Label>
              <Input
                value={data.taxExemptionReason}
                onChange={(e) => set({ taxExemptionReason: e.target.value })}
                placeholder={t('offer.tax.exemptionReasonPlaceholder')}
                data-testid="provider-tax-exemption-reason"
              />
            </div>
          ) : (
            <div>
              <Label className="text-xs text-slate-600">{t('offer.tax.rate')} *</Label>
              <Input
                type="number"
                min="0"
                max="100"
                step="0.1"
                value={data.taxRatePercent}
                onChange={(e) => set({ taxRatePercent: e.target.value })}
                placeholder={t('offer.tax.ratePlaceholder')}
                data-testid="provider-tax-rate"
              />
            </div>
          )}

          <p className="text-xs text-slate-500" data-testid="provider-tax-basis-hint">
            {data.taxBasis === TAX_BASIS_EXCLUSIVE
              ? t('offer.tax.hintExclusive', { sym: currencySym || '' })
              : t('offer.tax.hintInclusive', { sym: currencySym || '' })}
          </p>

          {/* Inline, so the provider sees WHICH field is missing even when the
              composer's global submit is disabled for an unrelated reason. */}
          {validateProviderTax(data) && (
            <p className="text-xs text-red-600" data-testid="provider-tax-validation-hint">
              {t(validateProviderTax(data))}
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export default ProviderTaxSection;
